/**
 * authedAxios — shared axios instance that attaches the Bearer token
 * from localStorage to every outbound request unless the caller has
 * already set its own Authorization header.
 *
 * Landed 2026-07-23 · IJ-070 hotfix · to close the Knowledge Atlas
 * 403 regression on prod (beta.search.inc): the July-23 deploy added
 * router-level auth to /api/ka/* endpoints but multiple KA frontend
 * components were still calling those endpoints without a token,
 * painting a false-empty Atlas UI for every user.
 *
 * Usage:
 *   import authedAxios from '../../lib/authedAxios';
 *   const r = await authedAxios.get('...');
 *
 * Rule for future KA / Atlas / admin-scoped calls: use `authedAxios`
 * (not the bare `axios` import) so no future caller can miss the header.
 */
import axios from 'axios';

const authedAxios = axios.create();

authedAxios.interceptors.request.use((config) => {
  try {
    const token = window.localStorage.getItem('startech_token');
    const hasAuth = !!(config.headers && (config.headers.Authorization || config.headers.authorization));
    if (token && !hasAuth) {
      config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
    }
  } catch (_) { /* localStorage unavailable — leave request unmodified */ }
  return config;
});

// Stale-token hardening (2026-07-23 · IJ-070 · added by operator OVERRIDE).
// If any authed call is rejected because the token in localStorage no longer
// fits the server (JWT_SECRET rotated on redeploy, session revoked, etc.),
// clear the stale token and send the user to /login rather than silently
// pretending they are a non-admin. Fires ONCE per page life · a second 401
// while already redirecting does nothing.
let _redirectingForAuth = false;
authedAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error && error.response && error.response.status;
      if ((status === 401 || status === 403) && !_redirectingForAuth) {
        const token = window.localStorage.getItem('startech_token');
        if (token) {
          _redirectingForAuth = true;
          window.localStorage.removeItem('startech_token');
          // Only redirect for the /auth/verify or /api/ka/* class of call —
          // do not hijack a 401 from an unrelated third-party endpoint.
          const url = (error.config && error.config.url) || '';
          if (url.includes('/api/auth/verify') || url.includes('/api/ka/')) {
            console.warn('[authedAxios] stale session token · clearing and redirecting to /login');
            if (window.location.pathname !== '/login') {
              window.location.assign('/login?reason=session-expired');
            }
          } else {
            // Not a KA / verify call — restore the token so unrelated 401s
            // from third-party services do not knock the user out.
            window.localStorage.setItem('startech_token', token);
            _redirectingForAuth = false;
          }
        }
      }
    } catch (_) { /* localStorage / window unavailable */ }
    return Promise.reject(error);
  },
);

export default authedAxios;
